import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import { api } from "../lib/api";
import { ImportUploadStep } from "./import/ImportUploadStep";
import { ImportMappingStep } from "./import/ImportMappingStep";
import { ImportReviewStep } from "./import/ImportReviewStep";
import { ImportReportStep } from "./import/ImportReportStep";

const STEPS = ["Upload", "Columns & homes", "Review & access", "Report"];

export default function DirectoryImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [aliases, setAliases] = useState({});
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (parsed) { setMapping(parsed.mapping); setAliases(parsed.home_aliases); }
  }, [parsed]);

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/directory-import/preview", { session_id: parsed.session_id, mapping, home_aliases: aliases });
      setPreview(data); setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };

  const commit = async (decisions) => {
    setBusy(true);
    try {
      const { data } = await api.post("/directory-import/commit", { session_id: parsed.session_id, decisions });
      setReport(data); setStep(3);
      toast.success(`Saved — ${data.counts.added} added, ${data.counts.updated} updated`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-5" data-testid="directory-import-page">
      <button onClick={() => navigate("/users")} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="import-back-button"><ArrowLeft size={15} /> User Management</button>
      <div>
        <h1 className="text-h2 flex items-center gap-2"><FileSpreadsheet size={22} className="text-sky-700" /> Import staff directory</h1>
        <p className="text-sm text-slate-500 mt-1">Upload a CSV or Excel file, confirm the columns, review every person's role and home access, then save. Nothing is written until the final step.</p>
      </div>
      <ol className="flex flex-wrap gap-2" data-testid="import-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`text-xs px-3 py-1.5 rounded-full border ${i === step ? "bg-sky-700 text-white border-sky-700" : i < step ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-white text-slate-500 border-slate-200"}`} data-testid={`import-step-${i}`}>{i + 1}. {s}</li>
        ))}
      </ol>

      {step === 0 && <ImportUploadStep onParsed={(d) => { setParsed(d); setStep(1); }} />}
      {step === 1 && parsed && (
        <ImportMappingStep parsed={parsed} mapping={mapping} setMapping={setMapping} aliases={aliases} setAliases={setAliases}
          onBack={() => setStep(0)} onNext={runPreview} busy={busy} />
      )}
      {step === 2 && preview && <ImportReviewStep preview={preview} onBack={() => setStep(1)} onCommit={commit} busy={busy} />}
      {step === 3 && report && <ImportReportStep report={report} onRestart={() => { setParsed(null); setPreview(null); setReport(null); setStep(0); }} />}
    </div>
  );
}
